import {
  DRILL_RATINGS,
  previewInterval,
  type DrillRating,
} from "@repo/swarved-shared";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Definition } from "../components/Definition";
import { EmptyState, Loading } from "../components/StateViews";
import type { RouterOutputs } from "../trpc";
import { trpc } from "../trpc";

type Card = RouterOutputs["drill"]["session"]["cards"][number];
type Deck =
  | { kind: "all" }
  | { kind: "study" }
  | { kind: "mandal"; mandal: number };
type Direction = "word" | "meaning";

const SESSION_SIZE = 20;
const NEW_PER_SESSION = 10;
/** A missed word comes back this many cards later in the same session. */
const AGAIN_GAP = 4;

const RATING_LABEL: Record<DrillRating, string> = {
  again: "Again",
  good: "Good",
  easy: "Easy",
};

function deckFromParam(value: string | null): Deck {
  if (value === "study") return { kind: "study" };
  const m = value?.match(/^m(\d+)$/);
  if (m) return { kind: "mandal", mandal: Number(m[1]) };
  return { kind: "all" };
}

function deckToParam(deck: Deck): string {
  if (deck.kind === "mandal") return `m${deck.mandal}`;
  return deck.kind;
}

export function DrillPage() {
  const [params, setParams] = useSearchParams();
  const deck = deckFromParam(params.get("deck"));
  const direction: Direction =
    params.get("dir") === "meaning" ? "meaning" : "word";

  const [session, setSession] = useState<Card[] | null>(null);
  const [starting, setStarting] = useState(false);
  const utils = trpc.useUtils();

  const update = (next: { deck?: Deck; direction?: Direction }) => {
    const d = next.deck ?? deck;
    const dir = next.direction ?? direction;
    const p: Record<string, string> = {};
    if (d.kind !== "all") p.deck = deckToParam(d);
    if (dir !== "word") p.dir = dir;
    setParams(p, { replace: true });
  };

  const start = async () => {
    setStarting(true);
    try {
      // A snapshot, not a live query: the session must not reshuffle
      // under the reader as their own reviews change what's due.
      const res = await utils.drill.session.fetch(
        { deck, size: SESSION_SIZE, newLimit: NEW_PER_SESSION },
        { staleTime: 0 },
      );
      setSession(res.cards);
      window.scrollTo({ top: 0 });
    } finally {
      setStarting(false);
    }
  };

  const finish = () => {
    setSession(null);
    window.scrollTo({ top: 0 });
    void utils.drill.overview.invalidate();
  };

  if (session) {
    return (
      <DrillSession
        cards={session}
        direction={direction}
        onExit={finish}
        onAgain={() => {
          finish();
          void start();
        }}
      />
    );
  }

  return (
    <DrillSetup
      deck={deck}
      direction={direction}
      onChange={update}
      onStart={() => void start()}
      starting={starting}
    />
  );
}

// ── Setup ───────────────────────────────────────────────────────────

function DrillSetup({
  deck,
  direction,
  onChange,
  onStart,
  starting,
}: {
  deck: Deck;
  direction: Direction;
  onChange: (next: { deck?: Deck; direction?: Direction }) => void;
  onStart: () => void;
  starting: boolean;
}) {
  const mandals = trpc.dohas.mandals.useQuery();
  const overview = trpc.drill.overview.useQuery({ deck });
  const o = overview.data;
  const fresh = o ? o.total - o.seen : 0;
  const upNext = o
    ? Math.min(SESSION_SIZE, o.due + Math.min(NEW_PER_SESSION, fresh))
    : 0;

  return (
    <div className="mx-auto max-w-3xl px-5 pt-14 sm:px-8">
      <p className="eyebrow">Drill</p>
      <h1 className="mt-3 font-serif text-4xl text-ink">Learn every word</h1>
      <p className="mt-3 max-w-2xl leading-relaxed text-ink-soft">
        Read the card, say the meaning to yourself, then flip it and be honest
        about how it went. Words you know come back less often. Words you miss
        come back soon.
      </p>

      <div className="mt-9">
        <p className="eyebrow">Which words</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ deck: { kind: "all" } })}
            className={pillClass(deck.kind === "all")}
          >
            Every word
          </button>
          <button
            type="button"
            onClick={() => onChange({ deck: { kind: "study" } })}
            className={pillClass(deck.kind === "study")}
          >
            Words in my study
          </button>
          {mandals.data?.map((m) => (
            <button
              key={m.mandal}
              type="button"
              onClick={() =>
                onChange({ deck: { kind: "mandal", mandal: m.mandal } })
              }
              className={pillClass(
                deck.kind === "mandal" && deck.mandal === m.mandal,
              )}
            >
              Mandal {m.mandal}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <p className="eyebrow">Show me</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ direction: "word" })}
            className={pillClass(direction === "word")}
          >
            The word, recall its meaning
          </button>
          <button
            type="button"
            onClick={() => onChange({ direction: "meaning" })}
            className={pillClass(direction === "meaning")}
          >
            The meaning, recall the word
          </button>
        </div>
      </div>

      <section className="mt-10 rounded-2xl border border-line bg-paper-raised p-6 sm:p-8">
        {overview.isLoading || !o ? (
          <Loading label="Counting your words" />
        ) : o.total === 0 ? (
          <EmptyState title="No words to drill here yet.">
            {deck.kind === "study" ? (
              <>
                Add dohas to your study and their words show up here.{" "}
                <Link to="/dohas" className="text-terracotta hover:underline">
                  Browse the dohas
                </Link>
              </>
            ) : null}
          </EmptyState>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-serif text-2xl text-ink">
                {o.seen.toLocaleString()}{" "}
                <span className="text-ink-faint">
                  of {o.total.toLocaleString()} words seen
                </span>
              </p>
              <p className="text-sm text-ink-faint">
                {percentLabel(o.seen, o.total)}
              </p>
            </div>
            <div
              className="mt-3 h-1.5 overflow-hidden rounded-full bg-paper-sunk"
              aria-hidden
            >
              <div className="flex h-full">
                <div
                  className="h-full bg-terracotta"
                  style={{ width: `${(o.mastered / o.total) * 100}%` }}
                />
                <div
                  className="h-full bg-terracotta/40"
                  style={{
                    width: `${((o.seen - o.mastered) / o.total) * 100}%`,
                  }}
                />
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-3 gap-4 text-center">
              <Stat label="Due now" value={o.due} accent={o.due > 0} />
              <Stat label="Learning" value={o.seen - o.mastered} />
              <Stat label="Mastered" value={o.mastered} />
            </dl>

            <div className="mt-8 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={onStart}
                disabled={starting || upNext === 0}
                className="rounded-full bg-ink px-8 py-3 font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {starting ? "Shuffling…" : `Start · ${upNext} cards`}
              </button>
              <p className="text-xs text-ink-faint">
                {upNext === 0
                  ? "Nothing is due. Come back later, or pick another set of words."
                  : `${Math.min(o.due, SESSION_SIZE)} to review, ${upNext - Math.min(o.due, SESSION_SIZE)} new. Most common words come first.`}
              </p>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd
        className={[
          "mt-1 font-serif text-3xl",
          accent ? "text-terracotta" : "text-ink",
        ].join(" ")}
      >
        {value.toLocaleString()}
      </dd>
    </div>
  );
}

// ── Session ─────────────────────────────────────────────────────────

function DrillSession({
  cards: initial,
  direction,
  onExit,
  onAgain,
}: {
  cards: Card[];
  direction: Direction;
  onExit: () => void;
  onAgain: () => void;
}) {
  const [queue, setQueue] = useState<Card[]>(initial);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [tally, setTally] = useState<Record<DrillRating, number>>({
    again: 0,
    good: 0,
    easy: 0,
  });
  const [saveFailed, setSaveFailed] = useState(false);
  const review = trpc.drill.review.useMutation({
    onError: () => setSaveFailed(true),
  });

  const card = queue[index] as Card | undefined;
  const done = !card;

  const rate = useCallback(
    (rating: DrillRating) => {
      if (!card || !flipped) return;
      review.mutate({ termId: card.termId, rating });
      setTally((t) => ({ ...t, [rating]: t[rating] + 1 }));
      if (rating === "again") {
        setQueue((q) => {
          const next = [...q];
          next.splice(Math.min(index + AGAIN_GAP, next.length), 0, {
            ...card,
            box: 1,
            isNew: false,
          });
          return next;
        });
      }
      setFlipped(false);
      setIndex((i) => i + 1);
    },
    [card, flipped, index, review],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const onControl = target?.closest("button, a, input, textarea");
      if ((e.key === " " || e.key === "Enter") && !onControl) {
        e.preventDefault();
        setFlipped((f) => !f);
        return;
      }
      const n = Number(e.key);
      if (n >= 1 && n <= DRILL_RATINGS.length) rate(DRILL_RATINGS[n - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rate]);

  if (initial.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-5 pt-20 sm:px-8">
        <EmptyState title="Nothing to drill right now.">
          Every word in this set is resting until its next review.
          <div className="mt-4">
            <button
              type="button"
              onClick={onExit}
              className="text-sm font-medium text-terracotta hover:underline"
            >
              Pick another set
            </button>
          </div>
        </EmptyState>
      </div>
    );
  }

  if (done) {
    const total = tally.again + tally.good + tally.easy;
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-5 pt-20 text-center sm:px-8">
        <span className="font-deva text-5xl leading-none text-terracotta">
          अ
        </span>
        <p className="eyebrow mt-6">Session done</p>
        <h1 className="mt-3 font-serif text-4xl text-ink">
          {total} {total === 1 ? "card" : "cards"} turned
        </h1>
        <p className="mt-3 leading-relaxed text-ink-soft">
          {tally.good + tally.easy} recalled, {tally.again} to see again.
        </p>
        {saveFailed ? (
          <p className="mt-3 text-sm text-terracotta">
            Some answers didn’t save. Check your connection and drill again.
          </p>
        ) : null}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={onAgain}
            className="rounded-full bg-ink px-6 py-2.5 text-sm font-medium text-paper transition-opacity hover:opacity-90"
          >
            Another round
          </button>
          <button
            type="button"
            onClick={onExit}
            className="rounded-full border border-line-strong px-6 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk"
          >
            Back to my progress
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-10 sm:px-8">
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onExit}
          className="text-sm text-ink-faint transition-colors hover:text-ink"
        >
          ← End session
        </button>
        <p className="text-sm text-ink-faint">
          {index + 1} of {queue.length}
        </p>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-paper-sunk">
        <div
          className="h-full bg-terracotta transition-[width] duration-300"
          style={{ width: `${(index / queue.length) * 100}%` }}
        />
      </div>

      <FlipCard
        key={`${card.termId}-${index}`}
        card={card}
        direction={direction}
        flipped={flipped}
        onFlip={() => setFlipped((f) => !f)}
      />

      <div className="mt-6 min-h-24">
        {flipped ? (
          <div className="grid grid-cols-3 gap-2">
            {DRILL_RATINGS.map((rating, i) => (
              <button
                key={rating}
                type="button"
                onClick={() => rate(rating)}
                className={[
                  "flex flex-col items-center rounded-2xl border px-3 py-3 transition-colors",
                  rating === "again"
                    ? "border-line-strong text-ink hover:bg-paper-sunk"
                    : rating === "good"
                      ? "border-ink bg-ink text-paper hover:opacity-90"
                      : "border-terracotta/50 bg-terracotta-wash text-terracotta hover:border-terracotta",
                ].join(" ")}
              >
                <span className="font-medium">{RATING_LABEL[rating]}</span>
                <span className="mt-0.5 text-xs opacity-70">
                  {previewInterval(card.box, rating)}
                  <span className="hidden sm:inline"> · {i + 1}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setFlipped(true)}
              className="w-full rounded-2xl border border-line-strong py-3.5 font-medium text-ink transition-colors hover:bg-paper-sunk"
            >
              {direction === "word" ? "Show the meaning" : "Show the word"}
            </button>
            <p className="hidden text-xs text-ink-faint sm:block">
              Space to flip, then 1, 2 or 3
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Card ────────────────────────────────────────────────────────────

const FACE =
  "[grid-area:1/1] flex min-h-80 flex-col rounded-3xl border border-line bg-paper-raised p-7 shadow-sm [backface-visibility:hidden] sm:p-9";

function FlipCard({
  card,
  direction,
  flipped,
  onFlip,
}: {
  card: Card;
  direction: Direction;
  flipped: boolean;
  onFlip: () => void;
}) {
  const front =
    direction === "word" ? (
      <WordFace card={card} />
    ) : (
      <MeaningFace card={card} />
    );

  return (
    <div className="mt-8 [perspective:1600px]">
      <div
        role="button"
        tabIndex={0}
        aria-label={flipped ? "Hide the answer" : "Reveal the answer"}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("a")) return;
          onFlip();
        }}
        className={[
          "grid cursor-pointer transition-transform duration-500 ease-out [transform-style:preserve-3d] motion-reduce:transition-none",
          flipped ? "[transform:rotateY(180deg)]" : "",
        ].join(" ")}
      >
        <div className={FACE} aria-hidden={flipped}>
          {front}
        </div>
        <div
          className={`${FACE} [transform:rotateY(180deg)]`}
          aria-hidden={!flipped}
        >
          <AnswerFace card={card} />
        </div>
      </div>
    </div>
  );
}

function CardTag({ card }: { card: Card }) {
  return <p className="eyebrow">{card.isNew ? "New word" : "Review"}</p>;
}

function WordFace({ card }: { card: Card }) {
  return (
    <>
      <CardTag card={card} />
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <p className="font-serif text-5xl leading-tight text-ink sm:text-6xl">
          {card.displayName}
        </p>
        {card.devanagari ? (
          <p className="mt-3 font-deva text-3xl text-ink-soft">
            {card.devanagari}
          </p>
        ) : null}
      </div>
      <p className="text-center text-xs text-ink-faint">What does it mean?</p>
    </>
  );
}

function MeaningFace({ card }: { card: Card }) {
  const [first, second] = card.meanings;
  return (
    <>
      <CardTag card={card} />
      <div className="flex flex-1 flex-col justify-center gap-4">
        {first ? (
          <p
            className={[
              "font-serif leading-snug text-ink",
              first.definition.length > 140
                ? "text-xl sm:text-2xl"
                : "text-2xl sm:text-3xl",
            ].join(" ")}
          >
            <Definition text={first.definition} />
          </p>
        ) : null}
        {second ? (
          <p className="font-serif text-lg leading-snug text-ink-soft">
            Also: <Definition text={second.definition} />
          </p>
        ) : null}
      </div>
      <p className="text-center text-xs text-ink-faint">Which word is this?</p>
    </>
  );
}

function AnswerFace({ card }: { card: Card }) {
  const extra = card.moreMeanings;
  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-serif text-3xl text-ink">{card.displayName}</p>
        {card.devanagari ? (
          <p className="font-deva text-xl text-ink-soft">{card.devanagari}</p>
        ) : null}
      </div>
      <ol className="mt-5 flex-1 space-y-4">
        {card.meanings.map((m, i) => (
          <li key={i} className="flex gap-3">
            {card.meanings.length > 1 ? (
              <span className="mt-1 font-mono text-xs text-terracotta">
                {i + 1}
              </span>
            ) : null}
            <div>
              <p className="leading-relaxed text-ink">
                <Definition text={m.definition} />
              </p>
              {m.dohaRefs.length > 0 ? (
                <p className="mt-1 flex flex-wrap gap-x-2 font-mono text-xs text-ink-faint">
                  {m.dohaRefs.slice(0, 4).map((ref) => (
                    <Link
                      key={ref}
                      to={`/doha/${ref}`}
                      className="transition-colors hover:text-terracotta"
                    >
                      {ref}
                    </Link>
                  ))}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      <Link
        to={`/glossary/${card.slug}`}
        className="mt-5 self-start text-sm font-medium text-terracotta hover:underline"
      >
        {extra > 0
          ? `${extra} more ${extra === 1 ? "meaning" : "meanings"} · open the word →`
          : "Open the word →"}
      </Link>
    </>
  );
}

function percentLabel(seen: number, total: number): string {
  if (seen > 0 && seen < total / 100) return "<1%";
  return `${Math.floor((seen / total) * 100)}%`;
}

function pillClass(active: boolean): string {
  return [
    "rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
    active
      ? "bg-ink text-paper"
      : "border border-line-strong text-ink-faint hover:bg-paper-sunk hover:text-ink",
  ].join(" ");
}
